import React, { createContext, useContext, useMemo, useReducer } from "react";

import { Audience } from "@/types";
import { compareReducer } from "./CompareReducer";
import {
  ADD_TO_COMPARE,
  CLEAR_COMPARE,
  CompareAction,
  CompareState,
  REMOVE_FROM_COMPARE,
  SET_COMPARE,
  initialState,
} from "./types";

/** Everything that changes the comparison, bound to the reducer */
export const bindActions = (dispatch: React.Dispatch<CompareAction>) => ({
  /** Adds an audience, at the given position if provided. Does nothing if it's already there. */
  add: (audience: Audience, index?: number) => dispatch({ type: ADD_TO_COMPARE, payload: { audience, index } }),
  /** Replaces the audiences being compared */
  set: (audiences: Audience[]) => dispatch({ type: SET_COMPARE, payload: audiences }),
  remove: (audience: Audience) => dispatch({ type: REMOVE_FROM_COMPARE, payload: audience }),
  clear: () => dispatch({ type: CLEAR_COMPARE }),
});

export type CompareActions = ReturnType<typeof bindActions>;

// State and actions are separate so components that only change the
// comparison (e.g. every audience card) don't re-render when it changes
const CompareStateContext = createContext<CompareState | undefined>(undefined);
const CompareActionsContext = createContext<CompareActions | undefined>(undefined);

export const CompareProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(compareReducer, initialState);

  // dispatch never changes, so neither do the actions
  const actions = useMemo(() => bindActions(dispatch), [dispatch]);

  return (
    <CompareActionsContext.Provider value={actions}>
      <CompareStateContext.Provider value={state}>
        {children}
      </CompareStateContext.Provider>
    </CompareActionsContext.Provider>
  );
};

/** The audiences being compared. Re-renders whenever they change. */
export const useCompareContext = () => {
  const state = useContext(CompareStateContext);
  if (state === undefined) {
    throw new Error("useCompareContext must be used within a CompareProvider");
  }
  return state;
};

/** Actions that change the comparison. Never changes identity. */
export const useCompareActions = () => {
  const actions = useContext(CompareActionsContext);
  if (actions === undefined) {
    throw new Error("useCompareActions must be used within a CompareProvider");
  }
  return actions;
};
